// src/pages/ProfilePage.tsx
//
// User profile page. Reachable via:
//   /u/{uid}           — direct uid form, always works
//   /u/@{username}     — username form, resolved via /usernames lookup
//
// Render contents:
//   • Avatar + display name + (@username) + counts (followers /
//     following / posts).
//   • Follow / Unfollow / Requested button (or "Edit profile" if
//     viewer is the owner).
//   • Item grid — public items only. Private accounts hide the
//     grid from non-followers (Instagram model).
//   • For owner: extra row to manage username + privacy.

import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Avatar,
  Box,
  Button,
  CircularProgress,
  Stack,
  Typography,
  IconButton,
} from '@mui/material';
import {
  ArrowBack,
  ContentCopy,
  LockOutlined,
} from '@mui/icons-material';

import { useT } from '../i18n/useT';
import { useAuthStore } from '../stores/authStore';
import { lookupUsername } from '../profile/username';
import { usePublicProfile } from '../profile/usePublicProfile';
import { useFollowState } from '../profile/useFollowState';
import {
  followUser,
  unfollowUser,
  cancelFollowRequest,
} from '../profile/follow';
import { ProfileItemGrid } from '../components/profile/ProfileItemGrid';
import { ProfileEditDialog } from '../components/profile/ProfileEditDialog';

export const ProfilePage: React.FC = () => {
  const t = useT();
  const navigate = useNavigate();
  const { handle } = useParams<{ handle: string }>();
  const { user, openAuthModal } = useAuthStore();

  // Resolve handle to a uid. Two forms:
  //   handle = "@daniel"  → username lookup
  //   handle = "abc123"   → already a uid
  const [resolvedUid, setResolvedUid] = useState<string | null>(null);
  const [resolveError, setResolveError] = useState<string | null>(null);
  useEffect(() => {
    if (!handle) {
      setResolvedUid(null);
      return;
    }
    let cancelled = false;
    (async () => {
      if (handle.startsWith('@')) {
        const lookup = await lookupUsername(handle.slice(1));
        if (cancelled) return;
        if (!lookup) {
          setResolveError('not_found');
          setResolvedUid(null);
        } else {
          setResolveError(null);
          setResolvedUid(lookup);
        }
      } else {
        // Treat as uid directly.
        setResolveError(null);
        setResolvedUid(handle);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [handle]);

  const { profile, loading } = usePublicProfile(resolvedUid);
  const followState = useFollowState(resolvedUid);

  const isSelf = !!user && !!resolvedUid && user.uid === resolvedUid;
  const [editOpen, setEditOpen] = useState(false);
  const [actionInFlight, setActionInFlight] = useState(false);

  // The grid is visible if: viewer is the owner, OR account is
  // public, OR account is private but viewer is a follower.
  const canSeeGrid =
    isSelf ||
    !profile?.is_account_private ||
    followState === 'following';

  const handleFollowClick = async () => {
    if (!user) return;
    if (user.isAnonymous) {
      // Sign-up gate per product decision: anonymous users can't
      // follow. Open the existing global auth modal in signup mode.
      openAuthModal('signup');
      return;
    }
    if (!resolvedUid) return;
    setActionInFlight(true);
    try {
      if (followState === 'following') {
        await unfollowUser(resolvedUid);
      } else if (followState === 'requested') {
        await cancelFollowRequest(resolvedUid);
      } else if (followState === 'not_following') {
        await followUser(resolvedUid);
      }
    } finally {
      setActionInFlight(false);
    }
  };

  const handleCopyLink = async () => {
    if (!profile) return;
    const handleSegment = profile.username
      ? `@${profile.username}`
      : resolvedUid;
    if (!handleSegment) return;
    const url = `${window.location.origin}/u/${encodeURIComponent(
      handleSegment
    )}`;
    try {
      await navigator.clipboard.writeText(url);
      // Toast/snackbar would be nicer; keep minimal for v1.
    } catch (err) {
      console.warn('copy failed:', err);
    }
  };

  if (loading || !resolvedUid) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '60vh',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (resolveError === 'not_found' || !profile) {
    return (
      <Box sx={{ p: 3, textAlign: 'center', pb: 10 }}>
        <Typography variant="h6">{t('User not found')}</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          {t('This profile may have been removed or never existed.')}
        </Typography>
        <Button onClick={() => navigate('/feed')} sx={{ mt: 2 }}>
          {t('Back to feed')}
        </Button>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        height: '100%',
        overflowY: 'auto',
        // AppShell renders floating frosted bars at top + bottom.
        // Pad to clear them so the back button and the last grid
        // row aren't hidden underneath. As you scroll, intermediate
        // grid rows visibly pass behind the frosted bars.
        paddingTop: 'var(--app-header-h)',
        paddingBottom: 'var(--app-bottom-nav-h)',
      }}
    >
      {/* Top bar */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 1,
          py: 1,
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <IconButton onClick={() => navigate(-1)} size="small">
          <ArrowBack />
        </IconButton>
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          {profile.username ? `@${profile.username}` : t('Profile')}
        </Typography>
      </Box>

      {/* Header — avatar, name, counts */}
      <Box sx={{ px: 2, pt: 2 }}>
        <Stack direction="row" spacing={2} alignItems="center">
          <Avatar
            src={
              profile.photo_url ||
              (isSelf ? user?.photoURL || undefined : undefined)
            }
            sx={{ width: 76, height: 76, fontSize: 28 }}
          >
            {(
              profile.display_name ||
              (isSelf ? user?.displayName : '') ||
              '?'
            )
              .trim()
              .charAt(0)
              .toUpperCase()}
          </Avatar>
          <Box sx={{ flex: 1 }}>
            <Stack direction="row" spacing={3}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                  {profile.posts_count ?? 0}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {t('Posts')}
                </Typography>
              </Box>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                  {profile.followers_count ?? 0}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {t('Followers')}
                </Typography>
              </Box>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                  {profile.following_count ?? 0}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {t('Following')}
                </Typography>
              </Box>
            </Stack>
          </Box>
        </Stack>

        <Box sx={{ mt: 1.5 }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
              {profile.display_name ||
                (isSelf && user?.displayName) ||
                t('Anonymous traveler')}
            </Typography>
            {profile.is_account_private && (
              <LockOutlined
                sx={{ fontSize: 14, color: 'text.secondary' }}
                titleAccess={t('Private account')}
              />
            )}
          </Stack>
          {profile.username && (
            <Typography variant="caption" color="text.secondary">
              @{profile.username}
            </Typography>
          )}
        </Box>

        {/* Action button row */}
        <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
          {isSelf ? (
            <>
              <Button
                variant="outlined"
                size="small"
                onClick={() => setEditOpen(true)}
                sx={{ flex: 1 }}
              >
                {t('Edit profile')}
              </Button>
              <Button
                variant="outlined"
                size="small"
                onClick={handleCopyLink}
                startIcon={<ContentCopy fontSize="small" />}
              >
                {t('Copy link')}
              </Button>
            </>
          ) : (
            <Button
              variant={followState === 'not_following' ? 'contained' : 'outlined'}
              size="small"
              disabled={actionInFlight || followState === 'unknown'}
              onClick={handleFollowClick}
              sx={{ flex: 1 }}
            >
              {followState === 'following' && t('Following')}
              {followState === 'requested' && t('Requested')}
              {followState === 'not_following' && t('Follow')}
              {followState === 'unknown' && t('Loading…')}
            </Button>
          )}
        </Box>
      </Box>

      {/* Item grid OR private placeholder */}
      <Box sx={{ mt: 3 }}>
        {canSeeGrid ? (
          <ProfileItemGrid ownerUid={resolvedUid} isSelf={isSelf} />
        ) : (
          <Box
            sx={{
              textAlign: 'center',
              p: 3,
              color: 'text.secondary',
            }}
          >
            <LockOutlined sx={{ fontSize: 32, mb: 1 }} />
            <Typography variant="subtitle2">
              {t('This account is private')}
            </Typography>
            <Typography variant="caption">
              {t('Follow to see their posts.')}
            </Typography>
          </Box>
        )}
      </Box>

      {isSelf && (
        <ProfileEditDialog
          open={editOpen}
          onClose={() => setEditOpen(false)}
          currentProfile={profile}
        />
      )}
    </Box>
  );
};
